import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { saveRecord } from "@/lib/records";
import {
  ArrowLeft,
  Flame,
  Beef,
  Wheat,
  Droplet,
  Sparkles,
  MapPin,
  Camera,
  Calculator,
  Loader2,
  Upload,
  ImageIcon,
  Info,
  Barcode as BarcodeIcon,
} from "lucide-react";
import { toast } from "sonner";
import BarcodeTab from "@/pages/nutrition/BarcodeTab";

const API = process.env.REACT_APP_BACKEND_URL;

const REGIONS = [
  { key: "south-indian", label: "South Indian", flag: "🌴" },
  { key: "north-indian", label: "North Indian", flag: "🏔️" },
  { key: "punjabi", label: "Punjabi", flag: "🚜" },
  { key: "gujarati", label: "Gujarati", flag: "🪁" },
  { key: "bengali", label: "Bengali", flag: "🐟" },
  { key: "kerala", label: "Kerala", flag: "🥥" },
  { key: "maharashtrian", label: "Maharashtrian", flag: "🥭" },
  { key: "hyderabadi", label: "Hyderabadi", flag: "🍚" },
  { key: "kashmiri", label: "Kashmiri", flag: "🍎" },
  { key: "goan", label: "Goan", flag: "🌊" },
  { key: "rajasthani", label: "Rajasthani", flag: "🐫" },
];

const ACTIVITY_LEVELS = [
  { key: "sedentary", label: "Sedentary", detail: "Little or no exercise" },
  { key: "light", label: "Light", detail: "1–3 days/week" },
  { key: "moderate", label: "Moderate", detail: "3–5 days/week" },
  { key: "active", label: "Active", detail: "6–7 days/week" },
  { key: "very_active", label: "Very active", detail: "Twice a day, heavy" },
];

const GOALS = [
  { key: "lose", label: "Lose fat" },
  { key: "maintain", label: "Maintain" },
  { key: "gain", label: "Gain muscle" },
];

const TABS = [
  { key: "calc", label: "Calorie & Macros", Icon: Calculator },
  { key: "regional", label: "Regional Plan", Icon: MapPin },
  { key: "photo", label: "Food Photo", Icon: Camera },
  { key: "barcode", label: "Barcode", Icon: BarcodeIcon },
];

export default function Nutrition() {
  const [tab, setTab] = useState("calc");

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      {/* Ambient */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(50% 30% at 20% 0%, rgba(52,211,153,0.14) 0%, transparent 60%), radial-gradient(50% 30% at 90% 20%, rgba(0,229,255,0.15) 0%, transparent 60%)",
        }}
      />

      {/* Top bar */}
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nutrition-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <Link
            to="/dashboard"
            data-testid="nutrition-to-dashboard"
            className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
          >
            Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-12">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-emerald-300/80">
            Chapter 04 · Nutrition Engine
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            What you eat,{" "}
            <span className="italic text-cyan-300">measured.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            Three tools, one plate. Calculate your calories &amp; macros, plan a
            week of regional Indian meals, or snap a photo to instantly see the
            nutrition on your plate.
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-white/5 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 sm:px-5 py-3 text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors shrink-0 whitespace-nowrap ${
                tab === t.key
                  ? "border-cyan-400 text-white"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}
            >
              <t.Icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Panels */}
        <div className="mt-10">
          <AnimatePresence mode="wait">
            {tab === "calc" && (
              <motion.div
                key="calc"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                <CalorieCalculator />
              </motion.div>
            )}
            {tab === "regional" && (
              <motion.div
                key="regional"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                <RegionalPlanner />
              </motion.div>
            )}
            {tab === "photo" && (
              <motion.div
                key="photo"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                <FoodPhotoAnalyzer />
              </motion.div>
            )}
            {tab === "barcode" && (
              <motion.div
                key="barcode"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                <BarcodeTab />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Calorie / Macro calculator
// ---------------------------------------------------------------------------
const CalorieCalculator = () => {
  const [form, setForm] = useState({
    age: 28,
    gender: "male",
    height_cm: 170,
    weight_kg: 70,
    activity: "moderate",
    goal: "maintain",
    diet_style: "vegetarian",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/nutrition/calc`, form);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] gap-6">
      {/* Form */}
      <form
        onSubmit={submit}
        data-testid="calc-form"
        className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 flex flex-col gap-4"
      >
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          Your body
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Age" testId="calc-age">
            <input
              type="number"
              min="10"
              max="120"
              value={form.age}
              onChange={(e) => setForm({ ...form, age: +e.target.value })}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            />
          </Field>
          <Field label="Gender" testId="calc-gender">
            <select
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Height (cm)" testId="calc-height">
            <input
              type="number"
              value={form.height_cm}
              onChange={(e) => setForm({ ...form, height_cm: +e.target.value })}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            />
          </Field>
          <Field label="Weight (kg)" testId="calc-weight">
            <input
              type="number"
              value={form.weight_kg}
              onChange={(e) => setForm({ ...form, weight_kg: +e.target.value })}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            />
          </Field>
        </div>

        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mt-2">
          Activity
        </div>
        <div className="grid grid-cols-1 gap-2" data-testid="calc-activity">
          {ACTIVITY_LEVELS.map((a) => (
            <label
              key={a.key}
              className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm cursor-pointer ${
                form.activity === a.key
                  ? "border-cyan-400 bg-cyan-400/10"
                  : "border-white/10 bg-white/5 hover:bg-white/10"
              }`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="activity"
                  className="accent-cyan-400"
                  checked={form.activity === a.key}
                  onChange={() => setForm({ ...form, activity: a.key })}
                />
                <span className="text-white font-medium">{a.label}</span>
              </div>
              <span className="text-xs text-zinc-400">{a.detail}</span>
            </label>
          ))}
        </div>

        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mt-2">
          Goal
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="calc-goal">
          {GOALS.map((g) => (
            <button
              type="button"
              key={g.key}
              onClick={() => setForm({ ...form, goal: g.key })}
              className={`rounded-lg border px-3 py-2 text-sm ${
                form.goal === g.key
                  ? "border-cyan-400 bg-cyan-400/10 text-white"
                  : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <Field label="Diet style" testId="calc-diet-style">
          <select
            value={form.diet_style}
            onChange={(e) => setForm({ ...form, diet_style: e.target.value })}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          >
            <option value="vegetarian">Vegetarian</option>
            <option value="non-vegetarian">Non-vegetarian</option>
            <option value="eggetarian">Eggetarian</option>
            <option value="vegan">Vegan</option>
          </select>
        </Field>

        <button
          data-testid="calc-submit"
          disabled={loading}
          className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-3 text-sm hover:bg-cyan-400 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Calculating
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" /> Compute my macros
            </>
          )}
        </button>
      </form>

      {/* Result */}
      <div data-testid="calc-result" className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 min-h-[420px]">
        {!result ? (
          <EmptyState
            Icon={Calculator}
            title="Answer a few questions"
            body="We'll compute your BMR, TDEE and macro split using Mifflin-St Jeor — the gold standard used by dietitians."
          />
        ) : (
          <MacrosResult data={result} />
        )}
      </div>
    </div>
  );
};

const MacrosResult = ({ data }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
      Your daily plan
    </div>
    <div className="mt-3 flex items-baseline gap-3">
      <div className="font-display text-6xl font-semibold tabular-nums text-white">
        {data.target_calories.toLocaleString()}
      </div>
      <div className="text-lg text-zinc-500">kcal / day</div>
    </div>
    <div className="mt-2 text-xs text-zinc-400">
      BMR {data.bmr} · TDEE {data.tdee} · Water {(data.water_ml / 1000).toFixed(1)} L
    </div>

    <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <MacroPill Icon={Beef} label="Protein" value={data.macros.protein_g} accent="#f87171" />
      <MacroPill Icon={Wheat} label="Carbs" value={data.macros.carbs_g} accent="#fbbf24" />
      <MacroPill Icon={Flame} label="Fat" value={data.macros.fat_g} accent="#a78bfa" />
      <MacroPill Icon={Droplet} label="Fiber" value={data.macros.fiber_g} accent="#34d399" />
    </div>

    <MacroBar macros={data.macros} />

    {data.notes?.length > 0 && (
      <div className="mt-6 flex flex-col gap-2">
        {data.notes.map((n, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-zinc-400 leading-relaxed">
            <Info className="w-3.5 h-3.5 mt-0.5 text-cyan-300 flex-shrink-0" />
            <span>{n}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const MacroPill = ({ Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      <Icon className="w-3 h-3" style={{ color: accent }} />
      {label}
    </div>
    <div className="mt-2 font-display text-2xl font-semibold tabular-nums" style={{ color: accent }}>
      {value}
      <span className="text-xs text-zinc-500 ml-1">g</span>
    </div>
  </div>
);

const MacroBar = ({ macros }) => {
  const p = macros.protein_g * 4;
  const c = macros.carbs_g * 4;
  const f = macros.fat_g * 9;
  const total = p + c + f || 1;
  return (
    <div className="mt-6">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
        Split of calories
      </div>
      <div className="flex h-3 rounded-full overflow-hidden bg-white/5">
        <div style={{ width: `${(p / total) * 100}%`, background: "#f87171" }} />
        <div style={{ width: `${(c / total) * 100}%`, background: "#fbbf24" }} />
        <div style={{ width: `${(f / total) * 100}%`, background: "#a78bfa" }} />
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-zinc-400">
        <span>Protein {Math.round((p / total) * 100)}%</span>
        <span>Carbs {Math.round((c / total) * 100)}%</span>
        <span>Fat {Math.round((f / total) * 100)}%</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2. Regional planner
// ---------------------------------------------------------------------------
const RegionalPlanner = () => {
  const [region, setRegion] = useState("south-indian");
  const [dietStyle, setDietStyle] = useState("vegetarian");
  const [conditions, setConditions] = useState("");
  const [allergies, setAllergies] = useState("");
  const [goal, setGoal] = useState("maintain");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  const generate = async () => {
    setLoading(true);
    setPlan(null);
    try {
      const { data } = await axios.post(`${API}/api/nutrition/regional-plan`, {
        region,
        diet_style: dietStyle,
        conditions,
        allergies,
        goal,
      });
      setPlan(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          Pick your region
        </div>
        <div data-testid="region-picker" className="mt-3 flex flex-wrap gap-2">
          {REGIONS.map((r) => (
            <button
              key={r.key}
              data-testid={`region-${r.key}`}
              onClick={() => setRegion(r.key)}
              className={`px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 border transition-colors ${
                region === r.key
                  ? "border-cyan-400 bg-cyan-400/10 text-white"
                  : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
              }`}
            >
              <span>{r.flag}</span>
              {r.label}
            </button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Diet style" testId="regional-diet-style">
            <select
              value={dietStyle}
              onChange={(e) => setDietStyle(e.target.value)}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="vegetarian">Vegetarian</option>
              <option value="non-vegetarian">Non-vegetarian</option>
              <option value="eggetarian">Eggetarian</option>
              <option value="vegan">Vegan</option>
            </select>
          </Field>
          <Field label="Goal" testId="regional-goal">
            <select
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            >
              {GOALS.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Conditions" testId="regional-conditions">
            <input
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              placeholder="diabetes, PCOS…"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            />
          </Field>
          <Field label="Allergies" testId="regional-allergies">
            <input
              value={allergies}
              onChange={(e) => setAllergies(e.target.value)}
              placeholder="peanuts, dairy…"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            />
          </Field>
        </div>

        <button
          data-testid="regional-submit"
          onClick={generate}
          disabled={loading}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-3 text-sm hover:bg-cyan-100 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Cooking your plan
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" /> Generate 7-day plan
            </>
          )}
        </button>
      </div>

      {plan && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-8"
          data-testid="regional-plan-result"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
                Personalized plan
              </div>
              <h3 className="font-display text-3xl font-semibold text-white mt-1">
                {plan.region_label}
              </h3>
              <div className="text-xs text-zinc-400 mt-1">{plan.diet_style}</div>
            </div>
          </div>
          {plan.hydration && (
            <div className="mb-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4 text-sm text-cyan-100 flex items-start gap-2">
              <Droplet className="w-4 h-4 mt-0.5 flex-shrink-0 text-cyan-300" />
              {plan.hydration}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {plan.days.map((d, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`plan-day-${i + 1}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                    {d.day}
                  </div>
                </div>
                <MealRow label="Breakfast" value={d.breakfast} />
                <MealRow label="Lunch" value={d.lunch} />
                <MealRow label="Snack" value={d.snack} />
                <MealRow label="Dinner" value={d.dinner} />
                {d.reason && (
                  <div className="mt-3 text-[11px] text-zinc-400 italic leading-relaxed">
                    {d.reason}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

const MealRow = ({ label, value }) => (
  <div className="border-t border-white/5 first:border-t-0 py-2">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}
    </div>
    <div className="text-sm text-white mt-0.5">{value || "—"}</div>
  </div>
);

// ---------------------------------------------------------------------------
// 3. Food photo analyzer
// ---------------------------------------------------------------------------
const FoodPhotoAnalyzer = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onPick = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Please upload a JPEG, PNG or WEBP image.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image is over 8 MB — try a smaller one.");
      return;
    }
    setFile(f);
    setResult(null);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const analyze = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await axios.post(
        `${API}/api/nutrition/food-photo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(data);
      if (!data.is_food) toast.warning("That doesn't look like food.");
      if (data.is_food && user) {
        saveRecord({
          type: "food",
          title: data.dish_name || "Food analysis",
          summary: data.health_note || "",
          payload: data,
        }).then((ok) => ok && toast.success("Saved to your private health history"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Uploader */}
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          Upload a food photo
        </div>
        <label
          data-testid="photo-dropzone"
          htmlFor="food-photo"
          className="mt-4 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/15 bg-black/40 aspect-[4/3] cursor-pointer hover:border-cyan-400/40 hover:bg-black/50 transition-colors overflow-hidden"
        >
          {preview ? (
            <img
              src={preview}
              alt="preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center px-6">
              <ImageIcon className="w-10 h-10 text-zinc-500 mx-auto" />
              <div className="mt-3 text-sm text-zinc-300">
                Drag a photo here, or tap to choose
              </div>
              <div className="mt-1 text-[11px] text-zinc-500">
                JPEG / PNG / WEBP · up to 8 MB
              </div>
            </div>
          )}
        </label>
        <input
          data-testid="photo-input"
          id="food-photo"
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => onPick(e.target.files?.[0])}
          className="hidden"
        />
        <div className="mt-4 flex gap-2">
          <button
            data-testid="photo-choose-btn"
            onClick={() => inputRef.current?.click()}
            className="flex-1 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {file ? "Choose another" : "Choose photo"}
          </button>
          <button
            data-testid="photo-analyze-btn"
            onClick={analyze}
            disabled={!file || loading}
            className="flex-1 rounded-full bg-emerald-400 text-black font-medium px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2 hover:bg-emerald-300 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Analyzing
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" /> Analyze
              </>
            )}
          </button>
        </div>
      </div>

      {/* Result */}
      <div
        data-testid="photo-result"
        className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 min-h-[420px]"
      >
        {!result ? (
          <EmptyState
            Icon={Camera}
            title="Snap. Estimate. Learn."
            body="Take a picture of your plate. Our AI vision estimates calories, portion size and macros — pointed at Indian cuisine."
          />
        ) : !result.is_food ? (
          <EmptyState
            Icon={ImageIcon}
            title="That doesn't look like food"
            body="Please upload a clearly visible dish or plate."
          />
        ) : (
          <FoodResult data={result} />
        )}
      </div>
    </div>
  );
};

const FoodResult = ({ data }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
      Detected · {data.confidence} confidence
    </div>
    <h3 className="mt-2 font-display text-3xl font-semibold text-white">
      {data.dish}
    </h3>
    {data.cuisine && (
      <div className="text-xs text-zinc-400 mt-1">
        {data.cuisine} · {data.portion}
      </div>
    )}
    <div className="mt-6 flex items-baseline gap-2">
      <div className="font-display text-5xl font-semibold text-white tabular-nums">
        {data.calories}
      </div>
      <div className="text-sm text-zinc-500">kcal (approx.)</div>
    </div>
    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <MacroPill Icon={Beef} label="Protein" value={data.protein_g} accent="#f87171" />
      <MacroPill Icon={Wheat} label="Carbs" value={data.carbs_g} accent="#fbbf24" />
      <MacroPill Icon={Flame} label="Fat" value={data.fat_g} accent="#a78bfa" />
      <MacroPill Icon={Droplet} label="Fiber" value={data.fiber_g} accent="#34d399" />
    </div>
    {data.health_note && (
      <div className="mt-6 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4 text-sm text-cyan-100 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-cyan-300" />
        {data.health_note}
      </div>
    )}
    <p className="mt-6 text-[11px] text-zinc-500 leading-relaxed">
      Estimates only. Actual values depend on portion, oil and ingredients used.
      Not medical advice.
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------
const Field = ({ label, testId, children }) => (
  <label data-testid={testId} className="block">
    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
      {label}
    </span>
    {children}
  </label>
);

const EmptyState = ({ Icon, title, body }) => (
  <div className="h-full flex flex-col items-center justify-center text-center px-6">
    <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
      <Icon className="w-6 h-6 text-cyan-300" />
    </div>
    <div className="mt-4 font-display text-lg font-semibold text-white">{title}</div>
    <p className="mt-2 text-sm text-zinc-400 max-w-sm leading-relaxed">{body}</p>
  </div>
);
